const warrantyMemory = {
  rules: null,
  rulesUrl: "",
  productsLoaded: false,
  products: {},
};

function warrantyNorm(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function warrantyRawValue(value) {
  if (value && typeof value === "object" && "value" in value) return value.value;
  return value;
}

function warrantySameValue(expected, actual) {
  actual = warrantyRawValue(actual);
  if (actual === undefined || actual === null || actual === "") return false;
  const expectedText = String(expected).trim();
  const actualText = String(actual).trim();
  const expectedNumber = Number(expectedText);
  const actualNumber = Number(actualText);
  if (expectedText !== "" && actualText !== "" && Number.isFinite(expectedNumber) && Number.isFinite(actualNumber)) {
    return expectedNumber === actualNumber;
  }
  return warrantyNorm(expectedText) === warrantyNorm(actualText);
}

function warrantyRulePriority(rule) {
  if (typeof rule.priority === "number") return rule.priority;
  const when = rule.when || {};
  let score = 0;
  if (Array.isArray(when.metafields) && when.metafields.length) score += 100;
  if (Array.isArray(when.require_tags) && when.require_tags.length) score += 50;
  if (Array.isArray(when.include_categories) && when.include_categories.length) score += 10;
  return score;
}

function warrantyRuleMatches(when, context) {
  if (when.categories === "applicable") {
    if (!context.category || !context.applicable.has(context.category)) return false;
  } else if (Array.isArray(when.categories) && when.categories.length) {
    if (!when.categories.some((category) => warrantyNorm(category) === context.category)) return false;
  }

  if (Array.isArray(when.include_categories) && when.include_categories.length) {
    if (!when.include_categories.some((category) => warrantyNorm(category) === context.category)) return false;
  }

  if (Array.isArray(when.exclude_categories) && when.exclude_categories.some((category) => warrantyNorm(category) === context.category)) {
    return false;
  }

  if (Array.isArray(when.exclude_tags) && when.exclude_tags.some((tag) => context.tags.includes(warrantyNorm(tag)))) {
    return false;
  }

  if (Array.isArray(when.require_tags) && when.require_tags.length) {
    if (!when.require_tags.every((tag) => context.tags.includes(warrantyNorm(tag)))) return false;
  }

  if (Array.isArray(when.metafields) && when.metafields.length) {
    for (const spec of when.metafields) {
      const separator = String(spec).indexOf("=");
      if (separator === -1) return false;
      const key = spec.slice(0, separator).trim();
      const expected = spec.slice(separator + 1).trim();
      if (!warrantySameValue(expected, context.metafields[key])) return false;
    }
  }

  return true;
}

function warrantyPickOffers(offers, priceCents) {
  const fitting = (offers || []).filter((offer) => {
    const max = Number(offer.max_price_cents);
    if (!Number.isFinite(max) || priceCents > max) return false;
    if (offer.min_price_cents != null && offer.min_price_cents !== "") {
      const min = Number(offer.min_price_cents);
      if (Number.isFinite(min) && priceCents <= min) return false;
    }
    return true;
  });

  const byTerm = new Map();
  fitting.forEach((offer) => {
    const term = offer.term_months ?? offer.sku;
    const current = byTerm.get(term);
    if (!current || Number(offer.max_price_cents) < Number(current.max_price_cents)) {
      byTerm.set(term, offer);
    }
  });

  return Array.from(byTerm.values());
}

function matchWarrantyOffers(doc, product) {
  const priceCents = Number(product?.priceCents);
  if (!doc || !Array.isArray(doc.rules) || !Number.isFinite(priceCents) || priceCents <= 0) return [];

  const context = {
    category: warrantyNorm(product.subCategory),
    tags: (product.tags || []).map(warrantyNorm),
    metafields: product.metafields || {},
    applicable: new Set((doc.applicable_categories || []).map(warrantyNorm)),
  };

  const matched = [];
  doc.rules.forEach((rule) => {
    if (!warrantyRuleMatches(rule.when || {}, context)) return;
    const offers = warrantyPickOffers(rule.offers, priceCents);
    if (!offers.length) return;
    matched.push({ rule, offers, priority: warrantyRulePriority(rule) });
  });

  if (!matched.length) return [];

  const topPriority = Math.max(...matched.map((entry) => entry.priority));
  const selected = [];
  matched
    .filter((entry) => entry.priority === topPriority)
    .forEach((entry) => {
      entry.offers.forEach((offer) => {
        selected.push(Object.assign({ ruleId: entry.rule.id }, offer));
      });
    });

  selected.sort((a, b) => (a.term_months || 0) - (b.term_months || 0) || String(a.sku).localeCompare(String(b.sku)));
  return selected;
}

function warrantyNumericId(id) {
  return String(id || "").replace(/^gid:\/\/shopify\/ProductVariant\//, "");
}

class ProductWarranties extends (typeof HTMLElement === "undefined" ? class {} : HTMLElement) {
  connectedCallback() {
    if (this.dataset.booted === "true") return;
    this.dataset.booted = "true";
    this.context = this.readContext();
    this.ignoreWarrantyWidget();
    this.bindVariantChange();
    this.bindCart();
    this.bindCashBackLayout();

    const start = () => {
      this.load().catch((error) => {
        console.error("[ProductWarranties] Failed to load warranties", error);
      });
    };

    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(start, { timeout: 1200 });
    } else {
      setTimeout(start, 1);
    }
  }

  readContext() {
    const data = this.readJson(this.querySelector("[data-warranty-product]")) || {};
    const metafields = {};
    const addonProduct = this.readJson(document.querySelector("product-addons [data-product-json]"));
    if (addonProduct?.metafields?.custom && typeof addonProduct.metafields.custom === "object") {
      Object.assign(metafields, addonProduct.metafields.custom);
    }
    if (data.bundleCount != null && data.bundleCount !== "") {
      metafields.number_of_items_in_bundle = data.bundleCount;
    }

    const subCategory = data.subCategory || warrantyRawValue(metafields.sub_category_name) || "";
    return {
      priceCents: Number(data.priceCents) || 0,
      subCategory,
      tags: Array.isArray(data.tags) ? data.tags : [],
      metafields,
      variants: Array.isArray(data.variants) ? data.variants : [],
    };
  }

  readJson(node) {
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error("[ProductWarranties] Invalid product JSON", error);
      return null;
    }
  }

  hideHulk(hidden) {
    const hulk = document.getElementById(`hulkapps_custom_options_${this.dataset.productId}`);
    if (hulk) hulk.hidden = hidden;
  }

  ignoreWarrantyWidget() {
    const disableWidget = () => {
      const hulk = document.getElementById(`hulkapps_custom_options_${this.dataset.productId}`);
      if (!hulk) return;
      hulk.hidden = true;
      hulk.querySelectorAll("input, select, textarea, button").forEach((field) => {
        field.disabled = true;
      });
    };

    disableWidget();
    const form = this.closest("[id^='shopify-section']")?.querySelector(".js-product-form-main");
    if (!form || form.dataset.warrantyWidgetIgnored === "true") return;
    form.dataset.warrantyWidgetIgnored = "true";
    new MutationObserver(disableWidget).observe(form, { childList: true, subtree: true });
  }

  bindVariantChange() {
    document.addEventListener("on:variant:change", (event) => {
      const variantId = event.detail?.variant?.id;
      if (variantId == null) return;
      const variant = this.context.variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return;
      this.context.priceCents = Number(variant.price) || 0;
      if (this.rules) this.renderOffers();
    });
  }

  bindCart() {
    const section = this.closest("[id^='shopify-section']");
    const form = section?.querySelector(".js-product-form-main");
    if (!form || form.dataset.warrantyCartBound === "true") return;
    form.dataset.warrantyCartBound = "true";

    form.addEventListener(
      "submit",
      (event) => {
        const selected = document.querySelector("product-warranties input[type='radio']:checked");
        if (!selected?.value) return;
        const addons = section.querySelector("product-addons");
        if (addons?.dataset.cartBound === "true") return;
        event.preventDefault();
        event.stopPropagation();
        this.submitWithWarranty(form, selected.value);
      },
      true
    );
  }

  async load() {
    this.rules = await this.loadRules(this.dataset.rulesUrl);
    await this.resolveBundleCount();
    this.dataset.ready = "true";
    await this.renderOffers();
  }

  /**
   * For Shopify Bundles, count appliance components (exclude kits/accessories).
   * Example: fridge + freezer + unification kit → 2 warranty items, not 3.
   */
  async resolveBundleCount() {
    const tags = (this.context.tags || []).map(warrantyNorm);
    if (!tags.includes("bundle")) return;

    const components = await this.fetchBundleComponents();
    if (!components.length) return;

    const applianceCount = components.reduce((total, component) => {
      if (this.isBundleAccessory(component)) return total;
      const quantity = Number(component.quantity) || 1;
      return total + quantity;
    }, 0);

    if (applianceCount > 0) {
      this.context.metafields.number_of_items_in_bundle = applianceCount;
      this.context.bundleCountSource = "components";
      this.context.bundleComponentCount = components.length;
      this.context.bundleApplianceCount = applianceCount;
    }
  }

  isBundleAccessory(component) {
    const title = warrantyNorm(component.title || component.productTitle || "");
    const sku = warrantyNorm(component.sku || "");
    if (!title && !sku) return false;
    if (/\bkit\b/.test(title)) return true;
    if (/\bunification\b/.test(title)) return true;
    if (/\baccessory\b/.test(title)) return true;
    if (sku === "zkun") return true;
    return false;
  }

  async fetchBundleComponents() {
    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;
    const handle = this.dataset.productHandle;
    const variantId = this.dataset.variantId;
    if (!apiUrl || !token) return [];

    const query = `
      query warrantyBundle($handle: String!) {
        product(handle: $handle) {
          variants(first: 25) {
            nodes {
              id
              components(first: 30) {
                nodes {
                  quantity
                  productVariant {
                    sku
                    title
                    product { title }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { handle } }),
      });
      if (!response.ok) return [];
      const payload = await response.json();
      const variants = payload?.data?.product?.variants?.nodes || [];
      const targetGid = variantId ? `gid://shopify/ProductVariant/${variantId}` : "";
      const variant =
        variants.find((item) => item.id === targetGid) ||
        variants.find((item) => (item.components?.nodes || []).length) ||
        variants[0];
      return (variant?.components?.nodes || []).map((node) => ({
        quantity: node.quantity,
        sku: node.productVariant?.sku || "",
        title: node.productVariant?.product?.title || node.productVariant?.title || "",
        productTitle: node.productVariant?.product?.title || "",
      }));
    } catch (error) {
      console.error("[ProductWarranties] Bundle component lookup failed", error);
      return [];
    }
  }

  async loadRules(url) {
    if (!url) throw new Error("Missing warranty rules URL");
    if (warrantyMemory.rules && warrantyMemory.rulesUrl === url) return warrantyMemory.rules;

    const cacheKey = `warranty-rules:v3:${url}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        warrantyMemory.rules = JSON.parse(cached);
        warrantyMemory.rulesUrl = url;
        return warrantyMemory.rules;
      }
    } catch (error) {
      sessionStorage.removeItem(cacheKey);
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Warranty rules HTTP ${response.status}`);
    const data = await response.json();
    warrantyMemory.rules = data;
    warrantyMemory.rulesUrl = url;
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (error) {
      /* sessionStorage can be unavailable or full; the in-memory copy still works. */
    }
    return data;
  }

  async renderOffers() {
    const token = (this.renderToken = (this.renderToken || 0) + 1);
    const previousVariantId = this.querySelector("input[type='radio']:checked")?.value || "";
    const offers = matchWarrantyOffers(this.rules, this.context);
    const list = this.querySelector(".product-warranties__list");
    if (!list) return;

    const logContext = {
      productId: this.dataset.productId,
      subCategory: this.context.subCategory,
      priceCents: this.context.priceCents,
      price: (this.context.priceCents / 100).toFixed(2),
      tags: this.context.tags,
      metafields: this.context.metafields,
    };

    if (!offers.length) {
      console.log("[ProductWarranties] No matching offers", logContext);
      list.innerHTML = "";
      this.hidden = true;
      this.hideHulk(true);
      this.syncSelection();
      return;
    }

    console.log("[ProductWarranties] Matched SKUs", {
      ...logContext,
      bundleCount: this.context.metafields.number_of_items_in_bundle,
      bundleCountSource: this.context.bundleCountSource || "metafield",
      bundleApplianceCount: this.context.bundleApplianceCount,
      skus: offers.map((offer) => offer.sku),
      offers: offers.map((offer) => ({
        sku: offer.sku,
        label: offer.label,
        termMonths: offer.term_months,
        ruleId: offer.ruleId,
        maxPriceCents: offer.max_price_cents,
        priceCents: offer.price_cents,
      })),
    });

    let resolved;
    try {
      resolved = await this.resolveSkus(offers);
    } catch (error) {
      console.error("[ProductWarranties] Failed to load warranty products", error);
      resolved = {};
    }

    if (token !== this.renderToken) return;

    const rows = offers
      .map((offer) => {
        const product = resolved[offer.sku];
        if (!product?.variantId || !/^\d+$/.test(String(product.variantId))) return null;
        const price = product.price ?? (offer.price_cents != null ? Number(offer.price_cents) / 100 : null);
        if (price == null) return null;
        return {
          offer,
          title: this.productTitle(product, offer),
          price,
          variantId: String(product.variantId),
        };
      })
      .filter(Boolean);

    const unresolved = offers
      .filter((offer) => !rows.some((row) => row.offer.sku === offer.sku))
      .map((offer) => offer.sku);

    console.log("[ProductWarranties] Resolved SKUs", {
      shown: rows.map((row) => ({
        sku: row.offer.sku,
        title: row.title,
        price: row.price,
        variantId: row.variantId,
      })),
      unresolved,
    });

    if (!rows.length) {
      list.innerHTML = "";
      this.hidden = true;
      this.syncSelection();
      return;
    }

    const radioName = `product-warranty-${this.dataset.productId}`;
    list.innerHTML = "";
    list.appendChild(this.createOption({
      radioName,
      title: "No Protection Plan",
      price: 0,
      variantId: "",
      checked: !previousVariantId,
      showPrice: false,
    }));
    rows.forEach((row) => {
      list.appendChild(this.createOption({
        radioName,
        title: row.title,
        price: row.price,
        variantId: row.variantId,
        checked: previousVariantId && row.variantId === previousVariantId,
        showPrice: true,
        cashBack: this.isCashBackOffer(row.offer, row.title),
      }));
    });

    this.hideHulk(true);
    this.hidden = false;
    this.syncSelection();
  }

  productTitle(product, offer) {
    const title = String(product?.title || "").trim();
    if (title) return title;
    return offer.label || offer.sku;
  }

  isCashBackOffer(offer, title) {
    if (Number(offer?.term_months) === 60) return true;
    const text = warrantyNorm(`${offer?.label || ""} ${title || ""}`);
    const fiveYear = text.includes("5 year") || text.includes("5-year");
    return fiveYear && (text.includes("50%") || text.includes("cash back"));
  }

  bindCashBackLayout() {
    this.cashBackMedia = window.matchMedia("(min-width: 769px) and (hover: hover) and (pointer: fine)");
    this.onCashBackLayoutChange = () => this.syncSelection();
    if (this.cashBackMedia.addEventListener) {
      this.cashBackMedia.addEventListener("change", this.onCashBackLayoutChange);
    }
  }

  usesHoverTooltip() {
    return this.cashBackMedia
      ? this.cashBackMedia.matches
      : window.matchMedia("(min-width: 769px) and (hover: hover) and (pointer: fine)").matches;
  }

  createOption({ radioName, title, price, variantId, checked, showPrice, cashBack }) {
    const option = document.createElement("label");
    option.className = "product-warranties__option";
    option.dataset.price = price;
    const note = String(this.dataset.cashBackNote || "").trim();
    const showCashBack = Boolean(cashBack && note);
    if (showCashBack) option.dataset.cashBack = "true";
    const noteId = showCashBack ? `warranty-cash-back-${this.dataset.productId}-${variantId || "plan"}` : "";
    const infoIcon = showCashBack
      ? `<span class="product-warranties__info" tabindex="0" aria-label="${this.escapeHtml(this.dataset.cashBackLabel || "Cash back details")}" aria-describedby="${this.escapeHtml(noteId)}"><svg class="product-warranties__info-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.25"></circle><path d="M8 7.25V11" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"></path><circle cx="8" cy="5.15" r="0.7" fill="currentColor"></circle></svg></span>`
      : "";
    const inlineNote = showCashBack
      ? `<span class="product-warranties__cash-back" hidden>${this.escapeHtml(note)}</span>`
      : "";
    const tooltip = showCashBack
      ? `<span class="product-warranties__tooltip" id="${this.escapeHtml(noteId)}" role="tooltip" hidden><span class="product-warranties__tooltip-text">${this.escapeHtml(note)}</span></span>`
      : "";
    const priceMarkup = showPrice
      ? `<span class="product-warranties__price">+${this.escapeHtml(this.formatPrice(price))}${infoIcon}</span>`
      : infoIcon;
    option.innerHTML = `
      <input class="product-warranties__input" type="radio" name="${radioName}" value="${this.escapeHtml(variantId)}"${checked ? " checked" : ""}>
      <span class="product-warranties__radio" aria-hidden="true"></span>
      <span class="product-warranties__label">
        <span class="product-warranties__name">${this.escapeHtml(title)}</span>
        ${priceMarkup}
        ${inlineNote}
      </span>
      ${tooltip}
    `;
    const input = option.querySelector("input");
    input.addEventListener("change", () => this.syncSelection());
    if (showCashBack) this.bindCashBackTooltip(option);
    return option;
  }

  bindCashBackTooltip(option) {
    const trigger = option.querySelector(".product-warranties__info");
    const tooltip = option.querySelector(".product-warranties__tooltip");
    if (!trigger || !tooltip) return;

    let hideTimer = 0;
    const show = () => {
      if (!this.usesHoverTooltip()) return;
      window.clearTimeout(hideTimer);
      tooltip.hidden = false;
      option.classList.add("is-note-open");
    };
    const hide = () => {
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        tooltip.hidden = true;
        option.classList.remove("is-note-open");
      }, 150);
    };

    trigger.addEventListener("mouseenter", show);
    trigger.addEventListener("mouseleave", hide);
    trigger.addEventListener("focus", show);
    trigger.addEventListener("blur", hide);
    tooltip.addEventListener("mouseenter", show);
    tooltip.addEventListener("mouseleave", hide);
  }

  displayTitle(productTitle, offer) {
    const title = String(productTitle || "").trim();
    if (title && warrantyNorm(title) !== warrantyNorm(offer.sku)) return title;
    return offer.label || title || offer.sku;
  }

  loadSkuCache() {
    if (warrantyMemory.productsLoaded) return;
    warrantyMemory.productsLoaded = true;
    try {
      const cached = sessionStorage.getItem("warranty-sku-cache-v5");
      if (cached) warrantyMemory.products = JSON.parse(cached);
    } catch (error) {
      warrantyMemory.products = {};
    }
  }

  saveSkuCache() {
    try {
      sessionStorage.setItem("warranty-sku-cache-v5", JSON.stringify(warrantyMemory.products));
    } catch (error) {
      /* Ignore quota errors. */
    }
  }

  async resolveSkus(offers) {
    this.loadSkuCache();
    const unique = [];
    const seen = new Set();
    offers.forEach((offer) => {
      if (!offer?.sku || seen.has(offer.sku)) return;
      seen.add(offer.sku);
      unique.push(offer);
    });
    const missing = unique.filter((offer) => !Object.prototype.hasOwnProperty.call(warrantyMemory.products, offer.sku));

    await Promise.all(missing.map(async (offer) => {
      warrantyMemory.products[offer.sku] = await this.resolveSku(offer.sku, offer.price_cents);
    }));
    if (missing.length) this.saveSkuCache();
    return warrantyMemory.products;
  }

  async resolveSku(sku, priceCents) {
    const expectedCents = priceCents == null || priceCents === "" ? null : Number(priceCents);

    const fromCatalog = await this.searchSku(sku, expectedCents);
    if (fromCatalog) return fromCatalog;

    const direct = await this.fetchProductJs(sku.toLowerCase());
    const directMatch = this.matchSku(direct, sku, expectedCents);
    if (directMatch) return directMatch;

    const params = new URLSearchParams({
      q: sku,
      "resources[type]": "product",
      "resources[limit]": "10",
      "resources[options][fields]": "title,variants.sku",
    });
    try {
      const response = await fetch(`/search/suggest.json?${params}`);
      if (response.ok) {
        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        for (const product of products) {
          if (!product?.handle) continue;
          const full = await this.fetchProductJs(product.handle);
          const match = this.matchSku(full, sku, expectedCents);
          if (match) return match;
        }
      }
    } catch (error) {
      console.error("[ProductWarranties] SKU search failed", error);
    }

    // Catalog sometimes has the right price but a wrong/missing SKU (e.g. APP3U500).
    if (expectedCents != null && Number.isFinite(expectedCents)) {
      const byPrice = await this.resolveByPrice(expectedCents, sku);
      if (byPrice) return byPrice;
    }

    return null;
  }

  async searchSku(sku, priceCents) {
    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;
    if (!apiUrl || !token || !sku) return null;

    const query = `
      query warrantySku($q: String!) {
        products(first: 10, query: $q) {
          edges {
            node {
              title
              handle
              variants(first: 10) {
                edges {
                  node {
                    id
                    sku
                    availableForSale
                    price { amount }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { q: sku } }),
      });
      if (!response.ok) return null;
      const payload = await response.json();
      const edges = payload?.data?.products?.edges || [];
      const matches = [];
      edges.forEach((edge) => {
        const product = edge?.node;
        if (!product) return;
        (product.variants?.edges || []).forEach((variantEdge) => {
          const variant = variantEdge?.node;
          if (!variant || variant.availableForSale === false) return;
          if (String(variant.sku || "").toUpperCase() !== String(sku).toUpperCase()) return;
          const variantId = warrantyNumericId(variant.id);
          if (!/^\d+$/.test(variantId)) return;
          const dollars = Number(variant.price?.amount);
          matches.push({
            variantId,
            title: product.title,
            price: Number.isFinite(dollars) ? dollars.toFixed(2) : "0",
            priceCents: Number.isFinite(dollars) ? Math.round(dollars * 100) : null,
            handle: String(product.handle || "").toLowerCase(),
          });
        });
      });
      return this.pickSkuMatch(matches, sku, priceCents);
    } catch (error) {
      console.error("[ProductWarranties] SKU search failed", error);
      return null;
    }
  }

  async resolveByPrice(expectedCents, sku) {
    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;
    if (!apiUrl || !token) return null;

    const query = `
      query warrantyByPrice($q: String!) {
        products(first: 50, query: $q) {
          edges {
            node {
              title
              handle
              tags
              variants(first: 5) {
                edges {
                  node {
                    id
                    sku
                    availableForSale
                    price { amount }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { q: "tag:warranty" } }),
      });
      if (!response.ok) return null;
      const payload = await response.json();
      const matches = [];
      (payload?.data?.products?.edges || []).forEach((edge) => {
        const product = edge?.node;
        if (!product) return;
        const tags = (product.tags || []).map(warrantyNorm);
        if (!tags.includes("warranty")) return;
        (product.variants?.edges || []).forEach((variantEdge) => {
          const variant = variantEdge?.node;
          if (!variant || variant.availableForSale === false) return;
          const dollars = Number(variant.price?.amount);
          const priceCents = Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
          if (priceCents !== expectedCents) return;
          const variantId = warrantyNumericId(variant.id);
          if (!/^\d+$/.test(variantId)) return;
          matches.push({
            variantId,
            title: product.title,
            price: dollars.toFixed(2),
            priceCents,
            handle: String(product.handle || "").toLowerCase(),
            sku: variant.sku || "",
          });
        });
      });

      if (!matches.length) return null;
      const skuHandle = String(sku || "").toLowerCase();
      matches.sort((a, b) => this.skuMatchScore(b, skuHandle, expectedCents) - this.skuMatchScore(a, skuHandle, expectedCents));
      const best = matches[0];
      console.log("[ProductWarranties] Resolved by price fallback", {
        sku,
        expectedCents,
        handle: best.handle,
        actualSku: best.sku,
        price: best.price,
      });
      return { variantId: best.variantId, title: best.title, price: best.price };
    } catch (error) {
      console.error("[ProductWarranties] Price fallback failed", error);
      return null;
    }
  }

  pickSkuMatch(matches, sku, priceCents) {
    if (!matches.length) return null;
    const skuHandle = String(sku).toLowerCase();
    const expected = priceCents == null ? null : Number(priceCents);

    // Prefer exact price matches from the rules spreadsheet. Reject mismatched
    // catalog prices so we can fall back (e.g. FLW3NLSP priced like APP3U15000).
    let candidates = matches;
    if (expected != null && Number.isFinite(expected)) {
      const priced = matches.filter((match) => match.priceCents === expected);
      if (priced.length) candidates = priced;
      else return null;
    }

    const ranked = candidates.slice().sort((a, b) => {
      return this.skuMatchScore(b, skuHandle, expected) - this.skuMatchScore(a, skuHandle, expected);
    });
    const best = ranked[0];
    return { variantId: best.variantId, title: best.title, price: best.price };
  }

  skuMatchScore(match, skuHandle, expectedCents) {
    let score = 0;
    if (expectedCents != null && match.priceCents === expectedCents) score += 100;
    if (match.handle.endsWith(skuHandle) || match.handle.includes(`-${skuHandle}`)) score += 10;
    if (skuHandle && warrantyNorm(match.sku || "") === warrantyNorm(skuHandle)) score += 5;
    return score;
  }

  async fetchProductJs(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      return null;
    }
  }

  matchSku(product, sku, expectedCents) {
    if (!product) return null;
    const variant = (product.variants || []).find((item) => {
      return String(item?.sku || "").toUpperCase() === String(sku).toUpperCase() && item.available !== false;
    });
    if (!variant) return null;
    const cents = Number(variant.price);
    if (
      expectedCents != null &&
      Number.isFinite(Number(expectedCents)) &&
      Number.isFinite(cents) &&
      cents !== Number(expectedCents)
    ) {
      return null;
    }
    return {
      variantId: warrantyNumericId(variant.id),
      title: product.title,
      price: Number.isFinite(cents) ? (cents / 100).toFixed(2) : "0",
    };
  }

  syncSelection() {
    let hasPaidPlan = false;
    const showInlineNote = !this.usesHoverTooltip();
    this.querySelectorAll(".product-warranties__option").forEach((option) => {
      const input = option.querySelector("input");
      const selected = Boolean(input?.checked);
      option.classList.toggle("is-selected", selected);
      const inlineNote = option.querySelector(".product-warranties__cash-back");
      if (inlineNote) inlineNote.hidden = !(showInlineNote && selected);
      // "No Protection Plan" has an empty value — only real plans show the note.
      if (selected && input?.value) hasPaidPlan = true;
    });

    const total = this.querySelector(".product-warranties__total");
    if (!total) return;
    total.hidden = !hasPaidPlan;
  }

  formatPrice(price) {
    return (Number(price) || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  async submitWithWarranty(form, warrantyVariantId) {
    const formData = new FormData(form);
    const quantity = parseInt(formData.get("quantity") || "1", 10);
    const variantId = parseInt(formData.get("id"), 10);
    if (!variantId) return;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[")) {
        const propName = key.slice("properties[".length, -1);
        if (propName === "Extended Warranty") continue;
        properties[propName] = value;
      }
    }

    const root = window.Shopify?.routes?.root || "/";
    const baseUrl = (typeof theme !== "undefined" && theme?.routes?.cartAdd) || `${root}cart/add`;
    const cartAddUrl = String(baseUrl).replace(/\/cart\/add\/?$/, "/cart/add.js");
    let warrantyAdded = false;

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: parseInt(warrantyVariantId, 10), quantity }],
        }),
      });
      warrantyAdded = response.ok;
    } catch (error) {
      console.error("[ProductWarranties] Failed to add warranty", error);
    }

    const mainItem = { id: variantId, quantity };
    if (Object.keys(properties).length) mainItem.properties = properties;

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [mainItem] }),
      });
      if (!response.ok) {
        if (warrantyAdded) await this.removeVariant(warrantyVariantId);
        return;
      }

      if (warrantyAdded) {
        localStorage.setItem(`_serviceVariants_${variantId}`, JSON.stringify([String(warrantyVariantId)]));
        localStorage.setItem(`_mainVariant_${warrantyVariantId}`, String(variantId));
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.refresh) cartDrawer.refresh(true);
      else document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
    } catch (error) {
      console.error("[ProductWarranties] Failed to add product", error);
      if (warrantyAdded) await this.removeVariant(warrantyVariantId);
    }
  }

  async removeVariant(variantId) {
    const root = window.Shopify?.routes?.root || "/";
    await fetch(`${root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: String(variantId), quantity: 0 }),
    });
  }
}

if (typeof customElements !== "undefined") {
  customElements.define("product-warranties", ProductWarranties);
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = { matchWarrantyOffers };
}
